/**
 * ADR-0221 — `useSiteReadinessSnapshot(site_id)`.
 *
 * Reads the persisted per-site readiness snapshot via
 * `GET /v1/sites/:site_id/readiness`. Exposes a stable `refresh()`
 * that fires `POST /v1/sites/:site_id/readiness/run` and then polls
 * the GET on a short interval until `in_progress === false`.
 *
 * The hook keeps the previous snapshot on screen while a refresh is
 * in flight (same "stale while revalidate" discipline as the old
 * `useReadinessSignals` hook, but authoritative from the server
 * rather than merged on the client).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { HttpClient } from '../api/http-client.js';
import {
  getSiteReadinessSnapshot,
  triggerSiteReadinessRun,
  type SiteReadinessSnapshot,
  type TriggerRunResult,
} from '../api/site-readiness-snapshot.js';

export interface UseSiteReadinessSnapshotState {
  readonly status: 'loading' | 'ready';
  readonly snapshot: SiteReadinessSnapshot | null;
  readonly refreshing: boolean;
  readonly last_refresh_failed: boolean;
  readonly rate_limited_until: string | null;
  /** Fire a new run + start polling. Returns the trigger outcome so
   *  the caller can surface a rate-limited toast. */
  readonly refresh: () => Promise<TriggerRunResult>;
}

const POLL_INTERVAL_MS = 2_500;
const POLL_MAX_ITERATIONS = 20; // 50 s cap, slightly past the 25 s probe deadline.

export function useSiteReadinessSnapshot(
  http: HttpClient | null,
  site_id: string | null,
): UseSiteReadinessSnapshotState {
  const [snapshot, setSnapshot] = useState<SiteReadinessSnapshot | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready'>('loading');
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshFailed, setLastRefreshFailed] = useState(false);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<string | null>(null);

  const mountedRef = useRef(true);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return (): void => {
      mountedRef.current = false;
      if (pollTimerRef.current !== null) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  const fetchOnce = useCallback(async (): Promise<SiteReadinessSnapshot | null> => {
    if (http === null || site_id === null) return null;
    const r = await getSiteReadinessSnapshot(http, site_id);
    if (r.kind === 'ok') return r.snapshot;
    return null;
  }, [http, site_id]);

  // Initial read.
  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    void fetchOnce().then((s) => {
      if (cancelled || !mountedRef.current) return;
      setSnapshot(s);
      setStatus('ready');
      // If the server fired its own page_load background run, start
      // polling so the UI reflects completion when it lands.
      if (s !== null && s.in_progress) {
        void poll(0);
      }
    });
    return (): void => {
      cancelled = true;
    };
    // `poll` is defined below but captured via closure — intentionally
    // omit from deps to avoid re-wiring the initial read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOnce]);

  async function poll(iteration: number): Promise<void> {
    if (!mountedRef.current || iteration >= POLL_MAX_ITERATIONS) {
      setRefreshing(false);
      return;
    }
    pollTimerRef.current = setTimeout(() => {
      void (async () => {
        const s = await fetchOnce();
        if (!mountedRef.current) return;
        if (s !== null) setSnapshot(s);
        if (s === null || s.in_progress) {
          void poll(iteration + 1);
        } else {
          setRefreshing(false);
        }
      })();
    }, POLL_INTERVAL_MS);
  }

  const refresh = useCallback(async (): Promise<TriggerRunResult> => {
    if (http === null || site_id === null) return { kind: 'error' };
    setRefreshing(true);
    setLastRefreshFailed(false);
    const r = await triggerSiteReadinessRun(http, site_id);
    if (!mountedRef.current) return r;
    if (r.kind === 'rate_limited') {
      setRefreshing(false);
      setLastRefreshFailed(true);
      const until = new Date(Date.now() + r.retry_after_seconds * 1_000).toISOString();
      setRateLimitedUntil(until);
      return r;
    }
    if (r.kind !== 'started') {
      setRefreshing(false);
      setLastRefreshFailed(true);
      return r;
    }
    setRateLimitedUntil(null);
    // Optimistic snapshot update — the server has NOT written yet, but
    // the UI can flip `in_progress` immediately.
    setSnapshot((prev) =>
      prev === null
        ? null
        : { ...prev, status: 'running', started_at: r.started_at, in_progress: true });
    void poll(0);
    return r;
  }, [http, site_id, fetchOnce]);

  return {
    status,
    snapshot,
    refreshing,
    last_refresh_failed: lastRefreshFailed,
    rate_limited_until: rateLimitedUntil,
    refresh,
  };
}
